import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { setPackageProblemResolution } from '@/api/packages';

/**
 * Close a delivery problem by saying how it was settled. The parcel stays on
 * its journey with that note and leaves every problem queue. A later carrier
 * status change reopens it on the server.
 */
export function ResolveProblemDialog({
  pkg,
  onOpenChange,
  onResolved,
}: {
  /** The parcel being resolved; null = closed. */
  pkg: { id: string; carrier: string | null; trackingCode: string; statusDetail: string | null } | null;
  onOpenChange: (open: boolean) => void;
  onResolved: () => void;
}) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (pkg) {
      setText('');
      setError(null);
    }
  }, [pkg]);

  const submit = async () => {
    if (!pkg || !text.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await setPackageProblemResolution(pkg.id, text.trim());
      onResolved();
      onOpenChange(false);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={pkg != null} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Resolve delivery problem</DialogTitle>
          <DialogDescription>
            {pkg?.carrier} {pkg?.trackingCode}{pkg?.statusDetail ? ` · ${pkg.statusDetail}` : ''}
          </DialogDescription>
        </DialogHeader>
        <Textarea
          autoFocus
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) void submit();
          }}
          placeholder="How it was settled, e.g. lost; vendor reshipped free"
          maxLength={500}
          rows={3}
        />
        {error && <p className="text-xs text-danger-ink">{error}</p>}
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button onClick={() => void submit()} disabled={busy || !text.trim()}>
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Resolve
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
